import { Expertise, DashboardStats, DashboardNotifications } from '../types/dashboard.types';

export const calculateStats = (expertises: Expertise[]): DashboardStats => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  // Get current week start (Monday) and end (Sunday)
  const currentDate = new Date();
  const dayOfWeek = currentDate.getDay();
  const diff = currentDate.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
  const weekStart = new Date(currentDate.setDate(diff));
  weekStart.setHours(0, 0, 0, 0);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  weekEnd.setHours(23, 59, 59, 999);
  
  const totalActive = expertises.length;
  
  const visitedThisWeek = expertises.filter(exp => {
    if (!exp.date_du_rv) return false;
    const visitDate = new Date(exp.date_du_rv);
    return visitDate >= weekStart && visitDate <= weekEnd;
  }).length;
  
  const pendingReportsData = expertises.filter(exp => {
    const status = (exp.status || '').toLowerCase();
    return status.includes('expert à achever') || status.includes('expert a achever');
  });
  const pendingReports = pendingReportsData.length;
  
  const twoDaysAgo = new Date(today.getTime() - 2 * 24 * 60 * 60 * 1000);
  const urgentTasks = pendingReportsData.filter(exp => {
    if (!exp.date_du_rv) return false;
    const visitDate = new Date(exp.date_du_rv);
    return visitDate < twoDaysAgo;
  }).length;

  return {
    totalActive,
    visitedThisWeek,
    pendingReports,
    urgentTasks
  };
};

export const processNotifications = (expertises: Expertise[]): DashboardNotifications => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Process urgent dossiers
  const pendingReportsData = expertises.filter(exp => {
    const status = (exp.status || '').toLowerCase();
    return status.includes('expert à achever') || status.includes('expert a achever');
  });
  
  const twoDaysAgo = new Date(today.getTime() - 2 * 24 * 60 * 60 * 1000);
  const urgentDossiersData = pendingReportsData.filter(exp => {
    if (!exp.date_du_rv) return false;
    const visitDate = new Date(exp.date_du_rv);
    return visitDate < twoDaysAgo;
  });
  
  const urgentDossiers = urgentDossiersData.slice(0, 5).map(exp => {
    const visitDate = new Date(exp.date_du_rv!);
    const daysSinceVisit = Math.floor((today.getTime() - visitDate.getTime()) / (1000 * 60 * 60 * 24));
    const maxDays = 7;
    const daysLeft = maxDays - daysSinceVisit;
    
    let badgeType: 'warning' | 'critical' = 'warning';
    if (daysLeft <= 0 || daysLeft <= 2) {
      badgeType = 'critical';
    }
    
    return {
      id: exp.id,
      ref: `EXP-${exp.id}`,
      property: formatAddress(exp.address),
      daysSinceVisit,
      daysLeft,
      badgeType,
      displayText: `${daysSinceVisit} jour${daysSinceVisit > 1 ? 's' : ''} depuis la visite`
    };
  });
  
  // Process next day schedule
  const nextDay = getNextDaySchedule(expertises, today);
  
  // Process pending reports
  const pendingReports = pendingReportsData.slice(0, 5).map(exp => {
    const daysAgo = exp.date_du_rv 
      ? Math.floor((today.getTime() - new Date(exp.date_du_rv).getTime()) / (1000 * 60 * 60 * 24))
      : 0;
    
    return {
      id: exp.id,
      ref: `EXP-${exp.id}`,
      property: formatAddress(exp.address),
      visitDate: exp.date_du_rv || '',
      daysAgo
    };
  });

  return {
    urgentDossiers,
    nextDay,
    pendingReports
  };
};

const formatAddress = (address: string | string[] | undefined): string => {
  if (!address) return 'Adresse non disponible';
  if (Array.isArray(address)) return address.join(', ');
  return address;
};

const getNextDaySchedule = (expertises: Expertise[], today: Date) => {
  const expertisesWithFutureDates = expertises
    .filter(exp => {
      if (!exp.date_du_rv) return false;
      const visitDate = new Date(exp.date_du_rv);
      return visitDate >= today;
    })
    .sort((a, b) => {
      const dateCompare = new Date(a.date_du_rv!).getTime() - new Date(b.date_du_rv!).getTime();
      if (dateCompare !== 0) return dateCompare;
      const timeA = a.heure_rv || '09:00';
      const timeB = b.heure_rv || '09:00';
      return timeA.localeCompare(timeB);
    });
  
  if (expertisesWithFutureDates.length === 0) return null;
  
  const firstVisitDate = new Date(expertisesWithFutureDates[0].date_du_rv!);
  const firstDateKey = firstVisitDate.toDateString();
  
  const dayExpertises = expertisesWithFutureDates.filter(exp => {
    const visitDate = new Date(exp.date_du_rv!);
    return visitDate.toDateString() === firstDateKey;
  });
  
  let dateLabel: string;
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  
  if (firstVisitDate.toDateString() === today.toDateString()) {
    dateLabel = "Aujourd'hui";
  } else if (firstVisitDate.toDateString() === tomorrow.toDateString()) {
    dateLabel = "Demain";
  } else {
    const options: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'long' };
    dateLabel = firstVisitDate.toLocaleDateString('fr-BE', options);
    dateLabel = dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1);
  }
  
  return {
    date: firstDateKey,
    label: dateLabel,
    count: dayExpertises.length,
    visits: dayExpertises.map(exp => ({
      id: exp.id,
      time: exp.heure_rv || '09:00',
      property: formatAddress(exp.address),
      client: exp.client?.name || 'Client non renseigné'
    }))
  };
};